/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import {
  formatNegativeAmount,
  formatPositiveAmount,
  formatRate
} from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { useTranslation } from 'react-i18next';
import OverflowTipography from 'src/components/OverflowTipography';
import { formatPaymentForm } from 'src/utils/contract';
import { getEquipmentLabel } from 'src/utils/equipments';
import {
  addPercentageToValue,
  getClientFullName,
  getClientPersonTypePrefix,
  roundNumberToDecimals
} from '../../../utils/common';

const ContractsRow = ({
  id,
  client,
  equipment,
  signatureDate,
  paymentForm,
  payday,
  firstRentDate,
  endDate,
  rate,
  residualPayment,
  initialPayment,
  equipmentCost,
  term,
  rent,
  folio,
  fideicomisoLabel
}) => {
  const clientName = useMemo(() => (client ? getClientFullName(client) : ''), [
    client
  ]);
  const { t } = useTranslation();

  return (
    <TableRow hover>
      <TableCell align="center">
        <Link to={`/contracts/${id}`}>{folio ?? t('COMMON.NO_DATA')}</Link>
      </TableCell>
      <TableCell
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        <OverflowTipography title={clientName}>
          <Link
            style={{ textDecoration: 'none' }}
            to={`/clients/detail/${client.id}`}
            maxWidth="200px"
          >
            {getClientPersonTypePrefix(client)}
            {clientName}
          </Link>
        </OverflowTipography>
      </TableCell>
      <TableCell align="center">
        {equipment ? (
          <OverflowTipography title={getEquipmentLabel(equipment)}>
            <Link
              style={{ textDecoration: 'none' }}
              to={`/inventory/${equipment.id}`}
            >
              {getEquipmentLabel(equipment)}
            </Link>
          </OverflowTipography>
        ) : (
          ''
        )}
      </TableCell>
      <TableCell align="center">{fideicomisoLabel ?? '-'}</TableCell>
      <TableCell align="center">
        {selectFormatDate(signatureDate, dateFormatType.MONTH_AND_YEAR)}
      </TableCell>
      <TableCell align="center">{t(formatPaymentForm(paymentForm))}</TableCell>
      <TableCell align="center">{payday ?? '-'}</TableCell>
      <TableCell align="center">
        {selectFormatDate(firstRentDate, dateFormatType.MONTH_AND_YEAR)}
      </TableCell>
      <TableCell align="center">
        {selectFormatDate(endDate, dateFormatType.MONTH_AND_YEAR)}
      </TableCell>
      <TableCell align="center">
        {formatPositiveAmount(roundNumberToDecimals(equipmentCost))}
      </TableCell>
      <TableCell align="center">
        {formatNegativeAmount(roundNumberToDecimals(initialPayment))}
      </TableCell>
      <TableCell align="center">
        {formatPositiveAmount(roundNumberToDecimals(equipmentCost - initialPayment))}
      </TableCell>
      <TableCell align="center">
        {formatPositiveAmount(roundNumberToDecimals(residualPayment))}
      </TableCell>
      <TableCell align="center">{term}</TableCell>
      <TableCell align="center">
        {addPercentageToValue(formatRate(rate))}
      </TableCell>
      <TableCell align="center">{formatPositiveAmount(rent)}</TableCell>
    </TableRow>
  );
};

ContractsRow.propTypes = {
  id: PropTypes.number.isRequired,
  client: PropTypes.object.isRequired,
  equipment: PropTypes.object.isRequired,
  signatureDate: PropTypes.string.isRequired,
  firstRentDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  rate: PropTypes.number.isRequired,
  initialPayment: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  rent: PropTypes.number.isRequired,
  equipmentCost: PropTypes.number.isRequired,
  residualPayment: PropTypes.number.isRequired,
  folio: PropTypes.string,
  fideicomisoLabel: PropTypes.string,
  paymentForm: PropTypes.string.isRequired,
  payday: PropTypes.number
};

export default ContractsRow;
